// src/client/renderer/vfx/items/phoenix-potion.ts
//
// Plan 17, Task 4.1 — Phoenix Potion pool (self-buff visual).
//
// Triggered by a SINGLE event:
//   ItemCastEvent with itemId === 'phoenix_potion' → phase='cast'.
//   (Heal is self-applied; no follow-up impact event.)
//
// Animation: 800ms total.
//   Phase 1 (0–400ms): green glow sprite scales 0→1.2 (rise from feet to head).
//   Phase 2 (400–800ms): opacity 1→0 (fade out).
//
// Pool: one-shot, max-active = 4. Standard F4/F5 lifecycle.
//
// Colour: 0x66ff99 (vibrant green).
//
// Registry tick contract (HF1):
//   pool.update(_deltaSeconds) IGNORES dt — does NOT call registry.update().
//   The caller (item-vfx-manager at Task 6.1) advances the shared registry once
//   per frame across all pools.
//
// Audio:
//   Manager fires the item cue only for phase='cast'. Phoenix Potion has a
//   single cast visual, so this remains one cue per use.

import * as THREE from 'three';
import type { GameEvent, ItemCastEvent } from '../../../../shared/game/types';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import { disposeSkillshotGroup } from './skillshot-shared';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_ACTIVE = 4;
const LIFETIME = 1.8;
const SCALE_PHASE_END = 0.35; // seconds where scale animation ends
const SCALE_HOLD_END = 1.2;
const GLOW_COLOUR = 0x66ff99;

// ─────────────────────────────────────────────────────────────────────────────
// Internal state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Build emitter
// ─────────────────────────────────────────────────────────────────────────────

function buildEmitter(
  anchor: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  // Glow sprite centred on the anchor
  const glowMaterial = new THREE.SpriteMaterial({
    color: GLOW_COLOUR,
    transparent: true,
    opacity: 1.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const glow = new THREE.Sprite(glowMaterial);
  glow.position.set(anchor.x, anchor.y, anchor.z);
  glow.scale.set(0, 0, 1);
  group.add(glow);

  // Sparkle sprite (slightly offset, same colour, smaller)
  const sparkleMaterial = new THREE.SpriteMaterial({
    color: GLOW_COLOUR,
    transparent: true,
    opacity: 0.8,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const sparkle = new THREE.Sprite(sparkleMaterial);
  sparkle.position.set(anchor.x, anchor.y + 0.3, anchor.z);
  sparkle.scale.set(0, 0, 1);
  group.add(sparkle);

  const handle = registry.create('phoenix_potion');
  state.handle = handle;
  const tl = handle.timeline;

  // Phase 1: scale 0→2.2 quickly, then hold before fading.
  tl.to(
    glow.scale,
    {
      x: 2.2,
      y: 2.2,
      z: 1,
      duration: SCALE_PHASE_END,
      ease: 'power2.out',
    },
    0,
  );
  tl.to(
    sparkle.scale,
    {
      x: 0.6,
      y: 0.6,
      z: 1,
      duration: SCALE_PHASE_END,
      ease: 'power2.out',
    },
    0,
  );

  tl.to(
    glow.scale,
    {
      x: 1.7,
      y: 1.7,
      z: 1,
      duration: SCALE_HOLD_END - SCALE_PHASE_END,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: 1,
    },
    SCALE_PHASE_END,
  );

  // Phase 2: opacity 1→0 after a visible hold.
  const opProxy = { value: 1.0 };
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: LIFETIME - SCALE_HOLD_END,
      ease: 'power1.in',
      onUpdate: () => {
        glowMaterial.opacity = opProxy.value;
        sparkleMaterial.opacity = opProxy.value * 0.8;
      },
    },
    SCALE_HOLD_END,
  );

  // Mark finished at end of animation
  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    LIFETIME,
  );

  return group;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createPhoenixPotionPool
// ─────────────────────────────────────────────────────────────────────────────

export function createPhoenixPotionPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene } = deps;
  const emitterStates = new Map<THREE.Object3D, EmitterState>();
  let disposed = false;

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;
    if (emitterStates.size >= MAX_ACTIVE) return null;

    const anchor = args.casterPosition ?? { x: 0, y: 0, z: 0 };
    const state: EmitterState = { handle: null!, finished: false };
    const group = buildEmitter(anchor, deps, state);
    scene.add(group);
    emitterStates.set(group, state);
    return group;
  }

  // ── update ───────────────────────────────────────────────────────────────
  // Registry tick contract (HF1): pool.update does NOT advance the registry.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  // ── isFinished ───────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = emitterStates.get(emitter);
    return state ? state.finished : true;
  }

  // ── release ──────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return;
    state.handle.dispose();
    scene.remove(emitter);
    disposeSkillshotGroup(emitter);
    emitterStates.delete(emitter);
  }

  // ── dispose (F4) ─────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const [emitter, state] of emitterStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    emitterStates.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolvePhoenixPotionForEvent
// ─────────────────────────────────────────────────────────────────────────────

export function resolvePhoenixPotionForEvent(
  event: GameEvent,
  getPositionForPlayer?: (playerId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'phoenix_potion'; args: ItemVfxSpawnArgs } | null {
  if (event.type !== 'item_cast') return null;

  const cast = event as ItemCastEvent;
  if (cast.itemId !== 'phoenix_potion') return null;

  return {
    poolId: 'phoenix_potion',
    args: {
      phase: 'cast',
      casterId: cast.casterId,
      casterPosition: getPositionForPlayer?.(cast.casterId),
    },
  };
}
